import React from 'react';
import { Box, Card, CardContent, Chip, CircularProgress, Grid, Paper, Radio, TextField, Typography } from '@mui/material';
import { People } from '@mui/icons-material';
import type { ExtraUserPricing } from '@components/config/generalConfigCalculations';

interface SignupExtraUsersSelectorProps {
  includedUsers: number;
  extraUsersCount: number;
  customUserCount: number;
  useCustomCount: boolean;
  extraUserPricing: ExtraUserPricing | undefined;
  loadingPricing: boolean;
  showCustomPricingFootnote: boolean;
  onSelectIncluded: () => void;
  onSelectPackage: (quantity: number) => void;
  onCustomFocus: () => void;
  onCustomCountChange: (rawValue: string) => void;
  calculateCustomExtraUsersCost: (extraUsers: number) => number;
}

/**
 * Seletor de usuários extras do plano Institucional no cadastro (pacotes pré-definidos +
 * quantidade personalizada). Extraído de SignupForm.tsx (F7).
 */
export const SignupExtraUsersSelector: React.FC<SignupExtraUsersSelectorProps> = ({
  includedUsers,
  extraUsersCount,
  customUserCount,
  useCustomCount,
  extraUserPricing,
  loadingPricing,
  showCustomPricingFootnote,
  onSelectIncluded,
  onSelectPackage,
  onCustomFocus,
  onCustomCountChange,
  calculateCustomExtraUsersCost,
}) => {
  return (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <People sx={{ mr: 1, color: 'primary.main' }} />
            Quantidade de Usuários
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Você tem {includedUsers || 3} usuários incluídos na assinatura.
            Selecione quantos usuários extras deseja contratar:
          </Typography>

          {loadingPricing ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper
                onClick={onSelectIncluded}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: (extraUsersCount || 0) === 0 && !useCustomCount ? 'primary.main' : 'divider',
                  bgcolor: (extraUsersCount || 0) === 0 && !useCustomCount ? 'primary.main' : 'background.paper',
                  color: (extraUsersCount || 0) === 0 && !useCustomCount ? 'white' : 'inherit',
                  '&:hover': {
                    bgcolor: (extraUsersCount || 0) === 0 && !useCustomCount ? 'primary.dark' : 'action.hover',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Radio checked={(extraUsersCount || 0) === 0 && !useCustomCount} sx={((extraUsersCount || 0) === 0 && !useCustomCount) ? { color: 'white', '&.Mui-checked': { color: 'white' } } : {}} />
                  <Typography sx={{ color: 'inherit' }}>Apenas usuários incluídos (recomendado para começar)</Typography>
                </Box>
              </Paper>
              {(extraUserPricing?.packages ?? []).map((pkg, index: number) => {
                const pricePerUser = pkg.price / pkg.quantity;
                const isSelected = (extraUsersCount || 0) === pkg.quantity && !useCustomCount;
                return (
                  <Paper
                    key={index}
                    onClick={() => onSelectPackage(pkg.quantity)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: 2,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'primary.main' : 'background.paper',
                      color: isSelected ? 'white' : 'inherit',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                        borderColor: 'primary.main',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Radio checked={isSelected} sx={isSelected ? { color: 'white', '&.Mui-checked': { color: 'white' } } : {}} />
                      <Typography sx={{ color: 'inherit' }}>
                        {pkg.quantity} usuários extras - R$ {pkg.price.toFixed(2)}/mês
                      </Typography>
                      <Chip
                        label={`R$ ${pricePerUser.toFixed(2)}/usuário`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                  </Paper>
                );
              })}
              <Paper
                onClick={() => {
                  const input = document.getElementById('signup-custom-extra-users-input');
                  if (input) {
                    input.focus();
                  }
                  onCustomFocus();
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: useCustomCount && customUserCount > 0 ? 'primary.main' : 'divider',
                  bgcolor: useCustomCount && customUserCount > 0 ? 'primary.main' : 'background.paper',
                  color: useCustomCount && customUserCount > 0 ? 'white' : 'inherit',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Radio checked={useCustomCount && customUserCount > 0} sx={(useCustomCount && customUserCount > 0) ? { color: 'white', '&.Mui-checked': { color: 'white' } } : {}} />
                  <Typography sx={{ color: 'inherit' }}>Outra quantidade:</Typography>
                  <TextField
                    id="signup-custom-extra-users-input"
                    type="number"
                    size="small"
                    value={customUserCount || ''}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCustomFocus();
                    }}
                    onChange={(e) => onCustomCountChange(e.target.value)}
                    onFocus={onCustomFocus}
                    placeholder="Quantidade"
                    inputProps={{
                      min: 0,
                      style: { width: '100px', textAlign: 'center' },
                    }}
                    sx={{ width: '120px' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    usuários extras
                  </Typography>
                  {customUserCount > 0 && (
                    <Chip
                      label={`R$ ${calculateCustomExtraUsersCost(customUserCount).toFixed(2)}/mês`}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Paper>
              {showCustomPricingFootnote && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: -1, display: 'block' }}>
                  * Quantidades customizadas sempre utilizam o preço unitário do maior pacote que seja menor ou igual à quantidade escolhida, multiplicado pela quantidade escolhida. Exemplo: Se você escolher 7 usuários extras e o maior pacote disponível menor que 7 for de 5 usuários (R$ 11.80/usuário), você será cobrado 7 × R$ 11.80 = R$ 82.60.
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.main', borderRadius: 1, color: 'white' }}>
            <Typography variant="body2" fontWeight="bold" sx={{ color: 'white' }}>
              Total de usuários: {extraUsersCount > 0 ? (includedUsers || 3) + extraUsersCount : includedUsers || 3}
            </Typography>
            {extraUsersCount > 0 && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'white' }}>
                ({includedUsers || 3} incluídos + {extraUsersCount} extras)
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default SignupExtraUsersSelector;
